/**
 * Following method adds logic to retry loading failed scripts upto 3 times
 * @param loadScriptMethod The method with script loading logic
 * @param url The url from where to load the script
 */
export const loadScriptWithRetries = (scriptUrl: string) => {
  let retriesLeft = 3;
  return (function retryScriptLoad(): Promise<{}> {
    return loadScript(scriptUrl).catch(() => {
      // If we exhaust the number of retries, return the original script load method so the script load promise can be rejected and logged by the appropriate handler
      retriesLeft = retriesLeft - 1;
      if (retriesLeft === 0) {
        return loadScript(scriptUrl);
      }
      return retryScriptLoad();
    });
  })();
};

async function loadScript(scriptUrl: string): Promise<{}> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;

    // Dynamically added scripts are async by default. By setting async to false, we are enabling the scripts
    // to be downloaded in parallel, but executed in order. This ensures that a script is executed after all of
    // its dependencies have been loaded and executed.
    script.async = false;

    // Charset needs to be specified when being used by service worker
    script.charset = 'utf-8';

    // This enables us to get callstacks for unhandled exceptions in browsers
    script.crossOrigin = 'anonymous';

    script.onload = resolve;
    script.onerror = () =>
      reject(new Error(`Failed to download the script at url: ${scriptUrl}, isUserOnline: ${window.navigator.onLine}`));

    // TODO: Will this work when the same export module (i.e. main) is used for multiple packages?
    document.head.appendChild(script);
  });
}
